/**
 * SearchAttentionPage — public web-attention dashboard for actively-traded
 * US equities. Aggregates Yahoo Trending + MSN/MarketWatch/CNBC headlines
 * and proposes a bullish / bearish / neutral bias per ticker.
 *
 * Wording note: this page explicitly frames its inputs as "public web
 * attention" — never as user-specific search history.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  RefreshCw, TrendingUp, TrendingDown, Minus, ChevronRight,
  Activity, Flame, Search, ExternalLink,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { API } from "../config/api";

const DIR_COLORS = {
  BULLISH:  { bg: "bg-emerald-500/15", border: "border-emerald-500/40", text: "text-emerald-300", dot: "bg-emerald-400" },
  BEARISH:  { bg: "bg-rose-500/15",    border: "border-rose-500/40",    text: "text-rose-300",    dot: "bg-rose-400" },
  NEUTRAL:  { bg: "bg-slate-500/15",   border: "border-slate-500/40",   text: "text-slate-300",   dot: "bg-slate-400" },
};
const DIR_ICON = { BULLISH: TrendingUp, BEARISH: TrendingDown, NEUTRAL: Minus };

function DirectionBadge({ direction }) {
  const cfg = DIR_COLORS[direction] || DIR_COLORS.NEUTRAL;
  const Icon = DIR_ICON[direction] || Minus;
  return (
    <span
      data-testid={`direction-${direction}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${cfg.bg} border ${cfg.border} ${cfg.text}`}
    >
      <Icon className="w-3 h-3" /> {direction}
    </span>
  );
}

function KpiTile({ label, value, tone = "slate", icon: Icon }) {
  const toneClasses = {
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
    rose:    "border-rose-500/40 bg-rose-500/10 text-rose-200",
    sky:     "border-sky-500/40 bg-sky-500/10 text-sky-200",
    amber:   "border-amber-500/40 bg-amber-500/10 text-amber-200",
    slate:   "border-slate-700 bg-slate-900/40 text-slate-200",
  }[tone];
  return (
    <div className={`rounded-md border px-3 py-2 ${toneClasses}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase opacity-80">{label}</div>
        {Icon && <Icon className="w-3 h-3 opacity-60" />}
      </div>
      <div className="text-xl font-bold tabular-nums truncate">{value}</div>
    </div>
  );
}

function AttentionBar({ score }) {
  const pct = Math.max(0, Math.min(100, score));
  const tone = pct >= 70 ? "bg-emerald-400" : pct >= 40 ? "bg-amber-400" : "bg-slate-500";
  return (
    <div className="w-24 h-2 bg-slate-800 rounded overflow-hidden">
      <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function ConfidencePill({ value }) {
  const tone = value >= 70 ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
             : value >= 40 ? "text-amber-300 border-amber-500/40 bg-amber-500/10"
             : "text-slate-400 border-slate-700 bg-slate-900/40";
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${tone}`}>
      {value}%
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return `${Math.floor(d)}s ago`;
  if (d < 3600) return `${Math.floor(d/60)}m ago`;
  if (d < 86400) return `${Math.floor(d/3600)}h ago`;
  return `${Math.floor(d/86400)}d ago`;
}

/* ──────────────────────────────────────────────────────────────────────────
 * MostSearchedRow — three side-by-side ranked lists:
 *   1. Yahoo Trending (current snapshot — not affected by timeframe)
 *   2. Google Finance Trends (filtered by selected timeframe)
 *   3. MSN Attention (mention-count proxy in selected timeframe)
 * ────────────────────────────────────────────────────────────────────────── */
function MostSearchedRow({ timeframe }) {
  const [yahoo, setYahoo] = useState(null);
  const [google, setGoogle] = useState(null);
  const [msn, setMsn] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [y, g, m] = await Promise.allSettled([
          axios.get(`${API}/search-attention/yahoo-trending`, { timeout: 30000 }),
          axios.get(`${API}/search-attention/google-finance-trending?timeframe=${timeframe}`, { timeout: 30000 }),
          axios.get(`${API}/search-attention/msn-attention?timeframe=${timeframe}`, { timeout: 15000 }),
        ]);
        if (cancelled) return;
        if (y.status === "fulfilled") setYahoo(y.value.data);
        if (g.status === "fulfilled") setGoogle(g.value.data);
        if (m.status === "fulfilled") setMsn(m.value.data);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [timeframe]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="most-searched-row">
      {/* YAHOO */}
      <Card className="bg-slate-900/40 border-slate-800" data-testid="ms-yahoo-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-white flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-purple-400" />
              Yahoo Trending
            </span>
            <span className="text-[10px] font-normal text-purple-300/80 border border-purple-500/40 bg-purple-500/10 rounded px-1.5 py-0.5">
              current snapshot
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 pt-0">
          <ul className="space-y-1" data-testid="ms-yahoo-list">
            {(yahoo?.items || []).slice(0, 10).map((it) => {
              const chg = it.change_pct;
              const chgColor = chg == null ? "text-slate-500" : (chg >= 0 ? "text-emerald-300" : "text-rose-300");
              return (
                <li key={it.symbol} className="flex items-center justify-between text-[11px] px-2 py-1 rounded hover:bg-slate-800/40"
                    data-testid={`ms-yahoo-row-${it.symbol}`}>
                  <span className="text-slate-500 font-mono w-5">{it.rank}</span>
                  <a href={`/stock?ticker=${it.symbol}`} className="font-mono font-bold text-cyan-300 hover:underline w-14">{it.symbol}</a>
                  <span className="font-mono text-slate-300 w-16 text-right">{it.price != null ? `$${Number(it.price).toFixed(2)}` : "—"}</span>
                  <span className={`font-mono w-14 text-right ${chgColor}`}>
                    {chg != null ? `${chg >= 0 ? "+" : ""}${Number(chg).toFixed(2)}%` : "—"}
                  </span>
                </li>
              );
            })}
            {!yahoo?.items?.length && (
              <li className="text-[11px] text-slate-500 px-2 py-3 text-center">No Yahoo trending data.</li>
            )}
          </ul>
        </CardContent>
      </Card>

      {/* GOOGLE */}
      <Card className="bg-slate-900/40 border-slate-800" data-testid="ms-google-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-white flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              Google Trends
            </span>
            <span className="text-[10px] font-normal text-amber-300/80 border border-amber-500/40 bg-amber-500/10 rounded px-1.5 py-0.5">
              {timeframe === "all" ? "all time" : `last ${timeframe}`}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 pt-0">
          <ul className="space-y-1" data-testid="ms-google-list">
            {(google?.items || []).slice(0, 10).map((it) => (
              <li key={`${it.ticker}-${it.rank}`} className="flex items-center justify-between text-[11px] px-2 py-1 rounded hover:bg-slate-800/40"
                  data-testid={`ms-google-row-${it.ticker}`}>
                <span className="text-slate-500 font-mono w-5">{it.rank}</span>
                <a href={`/stock?ticker=${it.ticker}`} className="font-mono font-bold text-cyan-300 hover:underline w-14">{it.ticker}</a>
                <span className="text-slate-400 text-[10px] flex-1 truncate" title={it.query}>{it.query}</span>
                <span className="font-mono text-amber-300 w-14 text-right">
                  {it.approx_traffic ? `${it.approx_traffic >= 1000 ? (it.approx_traffic/1000).toFixed(1)+"k" : it.approx_traffic}+` : "—"}
                </span>
                {!it.confirmed && (
                  <span className="ml-1 text-[9px] text-slate-500" title="Off-universe ticker — finance context inferred from news source">~</span>
                )}
              </li>
            ))}
            {google?.source_status && google.source_status !== "ok" && (
              <li className="text-[11px] text-amber-300/80 px-2 py-2 text-center bg-amber-500/5 rounded">
                {google.source_status === "unavailable" || google.source_status === "error"
                  ? "Google Trends unavailable right now."
                  : google.source_status === "rate_limited"
                  ? "Rate-limited by Google."
                  : "No finance-mapped trending searches in this window."}
              </li>
            )}
            {!google?.items?.length && google?.source_status === "ok" && (
              <li className="text-[11px] text-slate-500 px-2 py-3 text-center">No matching tickers in this window.</li>
            )}
          </ul>
        </CardContent>
      </Card>

      {/* MSN */}
      <Card className="bg-slate-900/40 border-slate-800" data-testid="ms-msn-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-white flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-sky-400" />
              MSN Attention
            </span>
            <span className="text-[10px] font-normal text-sky-300/80 border border-sky-500/40 bg-sky-500/10 rounded px-1.5 py-0.5">
              {timeframe === "all" ? "all time" : `last ${timeframe}`}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 pt-0">
          <ul className="space-y-1" data-testid="ms-msn-list">
            {(msn?.items || []).slice(0, 10).map((it) => {
              const dirColor = it.direction === "BULLISH" ? "text-emerald-300"
                : it.direction === "BEARISH" ? "text-rose-300" : "text-slate-400";
              return (
                <li key={it.ticker} className="flex items-center justify-between text-[11px] px-2 py-1 rounded hover:bg-slate-800/40"
                    data-testid={`ms-msn-row-${it.ticker}`}>
                  <span className="text-slate-500 font-mono w-5">{it.rank}</span>
                  <a href={`/stock?ticker=${it.ticker}`} className="font-mono font-bold text-cyan-300 hover:underline w-14">{it.ticker}</a>
                  <span className="font-mono text-sky-300 w-14 text-right">{it.mentions_count}</span>
                  <span className={`text-[10px] w-16 text-right ${dirColor}`}>{it.direction}</span>
                </li>
              );
            })}
            {!msn?.items?.length && (
              <li className="text-[11px] text-slate-500 px-2 py-3 text-center">No mentions in this window.</li>
            )}
          </ul>
          {msn?.label_note && (
            <div className="text-[9px] text-slate-600 italic px-2 pt-1.5 leading-snug">
              {msn.label_note}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function SearchAttentionPage() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [direction, setDirection] = useState("ALL");
  const [source, setSource] = useState("ALL");
  const [search, setSearch] = useState("");
  const [confMin, setConfMin] = useState(0);
  const [timeframe, setTimeframe] = useState("24h");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    // Hydrate from localStorage instantly so the page never feels empty
    try {
      const c = JSON.parse(localStorage.getItem("sp_search_attention_v1") || "null");
      if (c?.data) { setData(c.data); setSummary(c.summary || null); setLoading(false); }
    } catch {}

    try {
      const [r1, r2] = await Promise.allSettled([
        axios.get(`${API}/search-attention/signals?direction=${direction}&source=${source}&search=${search}&conf_min=${confMin}&timeframe=${timeframe}&limit=120`, { timeout: 30000 }),
        axios.get(`${API}/search-attention/summary?timeframe=${timeframe}`, { timeout: 15000 }),
      ]);
      if (r1.status === "fulfilled") setData(r1.value.data);
      if (r2.status === "fulfilled") setSummary(r2.value.data);
      if (r1.status === "fulfilled" || r2.status === "fulfilled") {
        try {
          localStorage.setItem("sp_search_attention_v1", JSON.stringify({
            data: r1.status === "fulfilled" ? r1.value.data : null,
            summary: r2.status === "fulfilled" ? r2.value.data : null,
            ts: Date.now(),
          }));
        } catch {}
      }
    } catch (e) {
      console.warn("Search Attention load fail:", e?.message);
    } finally {
      setLoading(false);
    }
  }, [direction, source, search, confMin, timeframe]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/search-attention/refresh`, null, { timeout: 90000 });
      await load();
    } catch {}
    setRefreshing(false);
  };

  const rows = data?.signals || [];
  const sources = useMemo(() => {
    const set = new Set(["ALL", "yahoo_trending"]);
    rows.forEach(r => (r.sources || []).forEach(s => set.add(s)));
    return Array.from(set);
  }, [rows]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/40 px-4 lg:px-8 py-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="search-attention-title">
              <Activity className="w-6 h-6 text-cyan-400" />
              Search Attention &amp; Bias
            </h1>
            <p className="text-xs text-slate-400 mt-1 max-w-3xl">
              Public finance attention signals across Yahoo Trending Tickers, MSN Money, MarketWatch and CNBC headlines.
              This panel surfaces <span className="text-slate-200 font-semibold">aggregated public web signals</span> — it does
              not access private or user-specific search behaviour.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500 hidden sm:block">
              Updated {data?.generated_at ? timeAgo(data.generated_at) : "—"}
            </span>
            <button
              onClick={refresh}
              disabled={refreshing}
              data-testid="sa-refresh-btn"
              className="text-xs px-3 py-1.5 rounded border border-slate-700 hover:bg-slate-800 flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Aggregating…" : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 lg:p-6 space-y-4 max-w-[1600px] mx-auto">
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <KpiTile label="Active tickers"  value={summary?.total_tickers ?? "—"} tone="slate" icon={Activity} />
          <KpiTile label="Bullish"         value={summary?.bullish_count ?? 0}    tone="emerald" icon={TrendingUp} />
          <KpiTile label="Bearish"         value={summary?.bearish_count ?? 0}    tone="rose" icon={TrendingDown} />
          <KpiTile label="Top attention"   value={summary?.top_attention?.ticker || "—"} tone="sky" icon={Flame} />
          <KpiTile label="Fastest rising"  value={summary?.fastest_rising?.ticker || "—"} tone="amber" icon={Flame} />
        </div>

        {/* Timeframe selector — narrows mentions to a recency window and
            re-derives attention / direction / confidence per ticker. */}
        <Card className="bg-slate-900/40 border-slate-800" data-testid="sa-timeframe-card">
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase text-slate-500 mr-1">Timeframe</span>
            <div className="flex items-center gap-1" data-testid="sa-timeframe-filter">
              {[
                { k: "1h",  l: "Last hour" },
                { k: "4h",  l: "Last 4 hours" },
                { k: "12h", l: "Last 12 hours" },
                { k: "24h", l: "Last 24 hours" },
                { k: "48h", l: "Last 48 hours" },
                { k: "7d",  l: "Last week" },
                { k: "all", l: "All" },
              ].map(t => (
                <button
                  key={t.k}
                  onClick={() => setTimeframe(t.k)}
                  data-testid={`sa-tf-${t.k}`}
                  className={`px-2.5 py-1 rounded border text-[11px] transition-colors ${
                    timeframe === t.k
                      ? "bg-cyan-600/30 border-cyan-500 text-white"
                      : "border-slate-700 text-slate-400 hover:text-white hover:border-slate-500"
                  }`}
                >
                  {t.l}
                </button>
              ))}
            </div>
            <span className="ml-auto text-[10px] text-slate-500">
              Showing signals scored from mentions inside the selected window.
            </span>
          </CardContent>
        </Card>

        {/* Yahoo / Google / MSN — most-searched / most-attention by source */}
        <MostSearchedRow timeframe={timeframe} />

        {/* Filter row */}
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1" data-testid="sa-direction-filter">
              {["ALL", "BULLISH", "BEARISH", "NEUTRAL"].map(d => (
                <button key={d}
                  onClick={() => setDirection(d)}
                  data-testid={`sa-dir-${d}`}
                  className={`px-2 py-1 rounded border text-[11px] ${direction === d ? "bg-cyan-600/30 border-cyan-500 text-white" : "border-slate-700 text-slate-400 hover:text-white"}`}
                >{d}</button>
              ))}
            </div>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              data-testid="sa-source-filter"
              className="bg-slate-900 border border-slate-700 rounded text-[11px] px-2 py-1"
            >
              {sources.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <div className="flex items-center gap-1 bg-slate-900 border border-slate-700 rounded px-2 py-1">
              <Search className="w-3 h-3 text-slate-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value.toUpperCase())}
                placeholder="Ticker…"
                className="bg-transparent text-[11px] outline-none w-20"
                data-testid="sa-search-input"
              />
            </div>
            <div className="flex items-center gap-2 text-[11px] text-slate-400">
              <label htmlFor="sa-conf-slider">Min conf:</label>
              <input
                id="sa-conf-slider"
                type="range" min={0} max={100} step={5}
                value={confMin}
                onChange={(e) => setConfMin(parseInt(e.target.value))}
                data-testid="sa-conf-slider"
                className="accent-cyan-500"
              />
              <span className="font-mono text-cyan-300 w-10">{confMin}%</span>
            </div>
            <div className="ml-auto text-[10px] text-slate-500">
              {data?.n_filtered ?? 0} of {data?.n_total ?? 0} signals shown
            </div>
          </CardContent>
        </Card>

        {/* Main ranked table */}
        <Card className="bg-slate-900/40 border-slate-800">
          <CardHeader>
            <CardTitle className="text-sm text-white">Ranked tickers — public attention</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sa-signals-table">
                <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-800 bg-slate-900/60 sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-3">#</th>
                    <th className="text-left py-2 px-3">Ticker</th>
                    <th className="text-left py-2 px-3">Direction</th>
                    <th className="text-left py-2 px-3">Confidence</th>
                    <th className="text-left py-2 px-3">Attention</th>
                    <th className="text-left py-2 px-3">Mentions</th>
                    <th className="text-left py-2 px-3">Sources</th>
                    <th className="text-right py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && rows.length === 0 && (
                    Array(8).fill(0).map((_, i) => (
                      <tr key={i} className="border-b border-slate-900">
                        <td colSpan={8} className="p-2">
                          <div className="h-8 bg-slate-800/40 rounded animate-pulse" />
                        </td>
                      </tr>
                    ))
                  )}
                  {!loading && rows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-slate-500">
                        No matching signals. Try widening the filters or click Refresh.
                      </td>
                    </tr>
                  )}
                  {rows.map((r) => {
                    const open = expanded === r.ticker;
                    return (
                      <>
                        <tr
                          key={r.ticker}
                          className={`border-b border-slate-900 hover:bg-slate-800/30 cursor-pointer ${open ? "bg-slate-800/40" : ""}`}
                          onClick={() => setExpanded(open ? null : r.ticker)}
                          data-testid={`sa-row-${r.ticker}`}
                        >
                          <td className="py-1.5 px-3 text-slate-500 font-mono text-[11px]">{r.rank}</td>
                          <td className="py-1.5 px-3 font-bold text-cyan-300 font-mono">
                            <a
                              href={`/stock?ticker=${r.ticker}`}
                              onClick={(e) => e.stopPropagation()}
                              className="hover:underline"
                            >{r.ticker}</a>
                          </td>
                          <td className="py-1.5 px-3"><DirectionBadge direction={r.direction} /></td>
                          <td className="py-1.5 px-3"><ConfidencePill value={r.confidence} /></td>
                          <td className="py-1.5 px-3">
                            <div className="flex items-center gap-2">
                              <AttentionBar score={r.attention_score} />
                              <span className="font-mono text-[11px] text-slate-300">{r.attention_score?.toFixed(0)}</span>
                            </div>
                          </td>
                          <td className="py-1.5 px-3 text-slate-300 text-[11px]">{r.mentions_count}</td>
                          <td className="py-1.5 px-3">
                            <div className="flex flex-wrap gap-1">
                              {(r.sources || []).slice(0, 3).map(s => (
                                <span key={s} className="text-[9px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700">
                                  {s.replace("www.", "").split(".")[0]}
                                </span>
                              ))}
                              {(r.sources || []).length > 3 && (
                                <span className="text-[9px] text-slate-500">+{r.sources.length - 3}</span>
                              )}
                            </div>
                          </td>
                          <td className="py-1.5 px-3 text-right">
                            <ChevronRight className={`w-3 h-3 text-slate-500 inline-block transition-transform ${open ? "rotate-90" : ""}`} />
                          </td>
                        </tr>
                        {open && (
                          <tr className="border-b border-slate-900 bg-slate-900/60">
                            <td colSpan={8} className="p-4 space-y-3" data-testid={`sa-detail-${r.ticker}`}>
                              {/* Direction rationale */}
                              <div className="flex flex-wrap gap-2 items-center">
                                <span className="text-[10px] uppercase text-slate-500">Direction rationale:</span>
                                {r.direction_matches?.length ? r.direction_matches.map((t, i) => (
                                  <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                                    t.startsWith("+")
                                      ? "bg-emerald-900/40 text-emerald-300"
                                      : "bg-rose-900/40 text-rose-300"
                                  }`}>{t}</span>
                                )) : (
                                  <span className="text-[11px] text-slate-500">
                                    Insufficient directional language — labelled neutral.
                                  </span>
                                )}
                              </div>
                              {/* Yahoo trending rank if available */}
                              {r.yahoo_rank && (
                                <div className="text-[11px] text-slate-400">
                                  Yahoo Trending rank: <span className="text-cyan-300 font-mono">#{r.yahoo_rank}</span>
                                </div>
                              )}
                              {/* Source mentions */}
                              <div>
                                <div className="text-[10px] uppercase text-slate-500 mb-1">Source mentions ({r.mentions?.length || 0})</div>
                                <div className="space-y-1">
                                  {r.mentions?.length ? r.mentions.map((m, i) => (
                                    <a
                                      key={i}
                                      href={m.link}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="block text-[11px] text-slate-300 hover:text-emerald-300 group"
                                    >
                                      <span className="inline-flex items-center gap-1">
                                        <ExternalLink className="w-3 h-3 opacity-60 group-hover:opacity-100" />
                                        <span className="text-slate-500 font-mono">{(m.source || "").replace("www.", "").split(".")[0]}</span>
                                        <span className="text-slate-600">·</span>
                                        <span className="text-slate-500">{timeAgo(m.seen_at)}</span>
                                      </span>
                                      <div className="text-slate-200 truncate ml-4">{m.title}</div>
                                    </a>
                                  )) : (
                                    <div className="text-[11px] text-slate-500">
                                      Surfaced from Yahoo trending alone (no headline corroboration yet).
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Source health */}
        {data?.sources && (
          <Card className="bg-slate-900/40 border-slate-800">
            <CardHeader>
              <CardTitle className="text-sm text-white">Source health</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {Object.entries(data.sources).map(([name, info]) => (
                  <div key={name} className="flex items-center justify-between p-2 rounded border border-slate-800 bg-slate-900/60">
                    <div>
                      <div className="text-xs text-slate-200 font-mono">{name}</div>
                      <div className="text-[10px] text-slate-500">checked {timeAgo(info.checked_at)}</div>
                    </div>
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${
                        info.status === "ok"
                          ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                          : info.status === "degraded"
                          ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
                          : "border-slate-600 text-slate-400"
                      }`}
                    >
                      {info.status} · {info.count}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-[10px] text-slate-600 text-center pt-2 pb-6">
          ⚠️ Public web attention signals only. No private or user-specific search data is accessed.
          Bullish / bearish bias is rules-based and informational — not investment advice.
        </p>
      </div>
    </div>
  );
}
